import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Modal } from '@components/index';
import { IPet } from '../../core/types';
import { isNearAdultTransition } from '../../utils/getEffectiveDogStage';

const MS_PER_DAY = 1000 * 60 * 60 * 24;
const MS_PER_WEEK = MS_PER_DAY * 7;
const WEEKS_SINCE_UPDATE_THRESHOLD = 3;
const DAYS_THRESHOLD = WEEKS_SINCE_UPDATE_THRESHOLD * 7;
const DISMISS_SILENCE_DAYS = 7;

export interface GrowthPhaseReminderModalProps {
  pet: IPet;
  onUpdateClick?: () => void;
}

export const GrowthPhaseReminderModal: React.FC<GrowthPhaseReminderModalProps> = ({
  pet,
  onUpdateClick,
}) => {
  const storageKey = `puppy_growth_reminder_${pet.id}`;

  const [dismissed, setDismissed] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return false;
      const lastDismissed = new Date(raw);
      return (Date.now() - lastDismissed.getTime()) / MS_PER_DAY < DISMISS_SILENCE_DAYS;
    } catch {
      return false;
    }
  });

  if (pet.dog_stage !== 'puppy') return null;

  // Yield priority to PuppyToAdultTransition when within 14 days of adult_when
  if (isNearAdultTransition(pet)) return null;

  const updatedAt = pet.updated_at ? new Date(pet.updated_at) : null;
  if (!updatedAt) return null;

  const now = new Date();
  const daysSinceUpdate = (now.getTime() - updatedAt.getTime()) / MS_PER_DAY;
  const isOpen = daysSinceUpdate >= DAYS_THRESHOLD && !dismissed;

  const handleClose = () => {
    setDismissed(true);
    try {
      localStorage.setItem(storageKey, new Date().toISOString());
    } catch {
      // localStorage unavailable (SSR / privacy mode)
    }
  };

  const handleUpdateClick = () => {
    setDismissed(true);
    onUpdateClick?.();
  };

  return (
    <Modal isOpen={isOpen} handleClose={handleClose}>
      <div className="growth-check-modal">
        <h3 className="growth-check-modal__title growth-check-modal__title--highlight">
          <FormattedMessage id="GROWTH.MODAL.TITLE" values={{ name: pet.name }} />
        </h3>

        <div className="growth-check-modal__info-card">
          <div className="growth-check-modal__info-row">
            <div className="growth-check-modal__info-icon growth-check-modal__info-icon--clock">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <div className="growth-check-modal__info-content">
              <span className="growth-check-modal__info-label">
                <FormattedMessage id="GROWTH.MODAL.LAST_UPDATE_LABEL" />
              </span>
              <span className="growth-check-modal__info-value">
                <FormattedMessage
                  id="GROWTH.MODAL.DAYS_AGO"
                  values={{
                    days: Math.floor(daysSinceUpdate),
                    strong: (chunks) => <strong>{chunks}</strong>,
                  }}
                />
              </span>
            </div>
          </div>
          <div className="growth-check-modal__info-divider" />
          <div className="growth-check-modal__info-row">
            <div className="growth-check-modal__info-icon growth-check-modal__info-icon--weight">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" />
                <path d="M12 6v6l4 2" />
              </svg>
            </div>
            <div className="growth-check-modal__info-content">
              <span className="growth-check-modal__info-label">
                <FormattedMessage id="GROWTH.MODAL.STORED_WEIGHT_LABEL" />
              </span>
              <span className="growth-check-modal__info-value">
                <strong>
                  <FormattedMessage id="PET.WEIGHT_KG" values={{ weight: pet.weight }} />
                </strong>
              </span>
            </div>
          </div>
        </div>

        <p className="growth-check-modal__hint">
          <FormattedMessage id="GROWTH.MODAL.HINT" values={{ name: pet?.name }} />
        </p>

        <div className="growth-check-modal__actions">
          <button
            type="button"
            className="growth-check-modal__btn-primary"
            onClick={handleUpdateClick}>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10" />
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
            </svg>
            <span>
              <FormattedMessage id="GROWTH.MODAL.CTA" />
            </span>
          </button>
          <button type="button" className="growth-check-modal__btn-secondary" onClick={handleClose}>
            <FormattedMessage id="GROWTH.MODAL.REMIND_LATER" />
          </button>
        </div>
      </div>
    </Modal>
  );
};
